// container component of Graph within Dashboard.
// This component contains
// 	- a graph title portion, which is the drag handle to move the graph around,
// 	- the graph area where one of the graphtypes (bar, line, etc) is displayed.
//	- The entire container can also be resized within the confines of dashboard

import React, { useEffect, useMemo, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import debounce from 'lodash/debounce';
import DashGraph from './DashGraph';
import { Dispatch } from 'redux';
import {
  updateDashGraphPosition,
  updateDashGraphSize,
  setHoveredTile,
  clearHoveredTile,
  setSelectedTile,
} from '../../redux/TabTile/TabActions';
import { updateSelectedTile } from '../../redux/TabTile/TabTileActionsAndMultipleDispatches';
import { Rnd } from 'react-rnd';
import { ColorSchemes } from '../ChartOptions/Color/ColorScheme';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux';
import useCombinedColorSchemes from '../../hooks/useCombinedColorSchemes';
import {
  updatePieChartStyleOptions,
  updateRoseChartStyleOptions,
  updateDoughChartStyleOptions,
  updateAreaChartStyleOptions,
  updateLineChartStyleOptions,
  updateBarChartStyleOptions,
  updateHorizontalBarChartStyleOptions,
  updateHorizontalStackedBarChartStyleOptions,
  updateStackedBarChartStyleOptions,
  updateStackedAreaChartStyleOptions,
  updateScatterChartStyleOptions,
  updateWaterfallChartStyleOptions,
  updateFunnelChartStyleOptions,
  updateTreeMapStyleOptions,
  updateGaugeManualColorWrapper,
  updateSankeyStyleOptions,
  updateCardControls,
  setColorScaleOption,
  updateGeoChartStyleOptions,
} from '../../redux/ChartPoperties/ChartControlsActions';
import Tooltip from '@mui/material/Tooltip';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Warning from '@mui/icons-material/WarningAmber';
import { fontSize } from '../..';

interface GraphRNDDashProps {
  backgroundColor: string;
  colorScheme: string; // Accept backgroundColor prop from parent
  softUI: boolean; // Other props like chart data, etc.
  dashboardHeight?: number; // Optional prop for dashboard height
  fontFamily?: string;
  setShowObjectsSidebar?: React.Dispatch<React.SetStateAction<boolean>>;
  setShowListofTileMenu?: (val: boolean) => void;
  setDashboardResizeColumn?: (val: boolean) => void;
  setShowDashboardFilterEdit?: (val: boolean) => void;
}

const GraphRNDDash = React.memo(
  ({
    style,
    setStyle,
    style2,
    fontFamily,

    backgroundColor, // Background color received as a prop
    setShowObjectsSidebar,
    setShowDashboardFilterEdit,
    setShowListofTileMenu,
    setDashboardResizeColumn,
    dashboardWidth, // Width of the dashboard, used to adjust graph position

    softUI,
    tabId,
    boxDetails,
    colorScheme,
    isHovered,
    isSelected,
    propKey,

    updateDashGraphPos,
    updateDashGraphSz,

    setMapJSON,
    setLoading,

    chartProp,
    tabTileProps,
    chartControls,

    tabState,
    isAnyTileInteracting,
    setInteractingTile,
  }: any) => {
    const gridSize = tabTileProps.dashGridSize;
    const dragGridX = gridSize.x;
    const dragGridY = gridSize.y;
    const resizeGridX = gridSize.x;
    const resizeGridY = gridSize.y;
    const allThemes = useCombinedColorSchemes();
    const [hovering, setHovering] = useState<boolean>(false);

    const [isDragging, setIsDragging] = useState(false);
    const [isResizing, setIsResizing] = useState(false);

    const tilesTodashboard = tabState.tabs[tabId]?.dashboardState?.tilesTodashboard;
    const tileIndividualScheme =
      chartControls.properties[boxDetails.propKey]?.colorScheme || 'peacock';
    const dashboardScheme = tabState.tabs[tabId].dashboardState.colorScheme || 'peacock';
    const selectedScheme = tabState.tabs[tabId].dashboardState.colorScheme
      ? tabState.tabs[tabId].dashboardState.colorScheme
      : 'peacock';
    const schemeToUse = tilesTodashboard ? tileIndividualScheme : dashboardScheme;
    const activeThemeObj = allThemes.find((el) => el.name === schemeToUse);
    const activeBackground = activeThemeObj?.background;
    if (activeBackground) {
      backgroundColor = activeBackground;
    }
    const dispatch = useDispatch();
    // local size for graph
    const [localSize, setLocalSize] = useState({
      width: boxDetails.width * gridSize.x,
      height: boxDetails.height * gridSize.y,
    });

    useEffect(() => {
      const currentChartType = chartProp.properties[boxDetails.propKey]?.chartType;
      const shouldBeManual = !!tilesTodashboard;

      let currentControls: any = null;
      let actionToDispatch: any = null;

      switch (currentChartType) {
        case 'pie':
          currentControls = chartControls.properties[boxDetails.propKey]?.pieChartControls;
          actionToDispatch = updatePieChartStyleOptions;
          break;
        case 'donut':
          currentControls = chartControls.properties[boxDetails.propKey]?.doughnutChartControls;
          actionToDispatch = updateDoughChartStyleOptions;
          break;
        case 'rose':
          currentControls = chartControls.properties[boxDetails.propKey]?.roseChartControls;
          actionToDispatch = updateRoseChartStyleOptions;
          break;
        case 'area':
          currentControls = chartControls.properties[boxDetails.propKey]?.areaChartControls;
          actionToDispatch = updateAreaChartStyleOptions;
          break;
        case 'line':
          currentControls = chartControls.properties[boxDetails.propKey]?.lineChartControls;
          actionToDispatch = updateLineChartStyleOptions;
          break;
        case 'multibar':
          currentControls = chartControls.properties[boxDetails.propKey]?.barChartControls;
          actionToDispatch = updateBarChartStyleOptions;
          break;
        case 'horizontalBar':
          currentControls =
            chartControls.properties[boxDetails.propKey]?.horizontalBarChartControls;
          actionToDispatch = updateHorizontalBarChartStyleOptions;
          break;
        case 'horizontalStacked':
          currentControls =
            chartControls.properties[boxDetails.propKey]?.horizontalStackedBarChartControls;
          actionToDispatch = updateHorizontalStackedBarChartStyleOptions;
          break;
        case 'stackedBar':
          currentControls = chartControls.properties[boxDetails.propKey]?.stackedBarChartControls;
          actionToDispatch = updateStackedBarChartStyleOptions;
          break;
        case 'stackedArea':
          currentControls = chartControls.properties[boxDetails.propKey]?.stackedAreaChartControls;
          actionToDispatch = updateStackedAreaChartStyleOptions;
          break;
        case 'scatterPlot':
          currentControls = chartControls.properties[boxDetails.propKey]?.scatterPlotChartControls;
          actionToDispatch = updateScatterChartStyleOptions;
          break;
        case 'waterfall':
          currentControls = chartControls.properties[boxDetails.propKey]?.waterfallChartControls;
          actionToDispatch = updateWaterfallChartStyleOptions;
          break;
        case 'funnel':
          currentControls = chartControls.properties[boxDetails.propKey]?.funnelChartControls;
          actionToDispatch = updateFunnelChartStyleOptions;
          break;
        case 'treeMap':
          currentControls = chartControls.properties[boxDetails.propKey]?.treeMapChartControls;
          actionToDispatch = updateTreeMapStyleOptions;
          break;
        case 'gauge':
          currentControls =
            chartControls.properties[boxDetails.propKey]?.axisOptions?.gaugeChartControls;
          actionToDispatch = updateGaugeManualColorWrapper;
          break;
        case 'sankey':
          currentControls = chartControls.properties[boxDetails.propKey]?.sankeyControls;
          actionToDispatch = updateSankeyStyleOptions;
          break;
        case 'simplecard':
          currentControls = chartControls.properties[boxDetails.propKey]?.cardControls;
          actionToDispatch = updateCardControls;
          break;
        case 'filledMap':
        case 'bubbleMap':
          currentControls = chartControls.properties[boxDetails.propKey]?.geoChartControls;
          actionToDispatch = updateGeoChartStyleOptions;
          break;
        case 'calendar':
        case 'heatmap':
          currentControls = chartControls.properties[boxDetails.propKey]?.colorScale;
          actionToDispatch = (pk: string, opt: string, val: any) =>
            setColorScaleOption(opt, val, pk);
          break;

        default:
          break;
      }
      if (currentControls && actionToDispatch) {
        if (currentControls.manualColor !== shouldBeManual) {
          dispatch(actionToDispatch(boxDetails.propKey, 'manualColor', shouldBeManual));
        }
      }
    }, [
      tilesTodashboard,
      boxDetails.propKey,
      chartProp.properties[boxDetails.propKey]?.chartType,
      dispatch,
    ]);

    const { tabs } = useSelector((state: RootState) => state?.tabState);

    const showWarning = useMemo(
      () => chartControls.properties[propKey]?.chartErrorDetails,
      [chartControls.properties[propKey]?.chartErrorDetails],
    );

    const showRowSecurity = Object.values(
      chartControls.properties?.[propKey]?.rowSecurity?.filterPanels || {},
    ).some((panel: any) => Array.isArray(panel.filters) && panel.filters.length > 0);

    const showColumnSecurity = Object.values(
      chartControls.properties?.[propKey]?.columnSecurity?.filterPanels || {},
    ).some((panel: any) => Array.isArray(panel.filters) && panel.filters.length > 0);
    const { allTileBorder } = tabs[tabId];
    const isAllTileBorder = allTileBorder?.isBorder;
    const border = isAllTileBorder
      ? `solid ${allTileBorder?.borderProperties?.width}px ${allTileBorder?.borderProperties?.color}`
      : '';
    const borderRadius = isAllTileBorder
      ? `${allTileBorder?.borderProperties?.borderRadius}px`
      : '';
    if (chartProp.properties[boxDetails.propKey].chartType === 'richText') {
      let chartThemes = allThemes.filter((el) => el.name === schemeToUse);
      backgroundColor = chartThemes?.[0]?.background;
    } else if (chartProp.properties[boxDetails.propKey].chartType === 'sankey') {
      if (
        chartControls.properties[boxDetails.propKey].sankeyControls.manualColor &&
        chartControls.properties[boxDetails.propKey].sankeyControls.backgroundColor
      ) {
        backgroundColor =
          chartControls.properties[boxDetails.propKey].sankeyControls.backgroundColor;
      }
    } else if (
      chartProp.properties[boxDetails.propKey].chartType === 'calendar' ||
      chartProp.properties[boxDetails.propKey].chartType === 'heatmap'
    ) {
      if (chartControls.properties[boxDetails.propKey].colorScale?.manualColor) {
        let chartThemes = allThemes.filter(
          (el) => el.name === chartControls.properties[boxDetails.propKey].colorScheme,
        );
        backgroundColor = chartThemes?.[0]?.background;
      }
    } else if (
      chartProp.properties[boxDetails.propKey].chartType === 'filledMap' ||
      chartProp.properties[boxDetails.propKey].chartType === 'bubbleMap'
    ) {
      if (chartControls.properties[boxDetails.propKey].geoChartControls.manualColor) {
        let chartThemes = ColorSchemes.filter(
          (el) => el.name === chartControls.properties[boxDetails.propKey].colorScheme,
        );
        backgroundColor = chartThemes?.[0]?.background;
      }
    } else if (chartProp.properties[boxDetails.propKey].chartType === 'gauge') {
      if (
        chartControls.properties[boxDetails.propKey].axisOptions?.gaugeChartControls?.manualColor
      ) {
        let chartThemes = allThemes.filter(
          (el) => el.name === chartControls.properties[boxDetails.propKey].colorScheme,
        );
        backgroundColor = backgroundColor;
      }
    }

    const skeuomorphicStyles = softUI
      ? {
          borderRadius: '12px',
          backgroundColor: !backgroundColor ? 'white' : backgroundColor,
          transistion: 'all 0.3s ease-in-out',
          textShadow: '2px 2px 7px rgba(0,0,0,0.2)',
          fontSize: '16px',
        }
      : {};

    const extendedStyle = {
      ...style,
      backgroundColor: !backgroundColor ? 'white' : backgroundColor,
      ...(boxDetails.highlight || hovering ? skeuomorphicStyles : {}),
      ...(border ? { border } : {}),
      ...(borderRadius ? { borderRadius } : {}),
    };

    const extendedStyle2 = {
      ...style2,
      backgroundColor: !backgroundColor ? 'white' : backgroundColor,
      ...(boxDetails.highlight || hovering ? skeuomorphicStyles : {}),
    };

    const innerSkeuomorphicStyles = softUI
      ? {
          //boxShadow: `
          //inset 6px 6px 20px rgba(256, 256, 256, 0.6),   /* Bottom-right shadow */
          //inset -6px -6px 20px rgba(190, 190, 190, 0.6),  /* Top-left shadow */
          //inset 6px -6px 20px rgba(190, 190, 190, 0.6),  /* Bottom-left shadow */
          // inset -6px 6px 20px rgba(190, 190, 190, 0.6) /* Top-right shadow */
          //`,

          borderRadius: '3px',
        }
      : {};
    // this adds debounce to updateRedux
    const debouncedUpdateRedux = useMemo(
      () =>
        debounce((tabId, propKey, x, y, w, h) => {
          updateDashGraphSz(tabId, propKey, x, y, w, h);
        }, 150),
      [updateDashGraphSz],
    );
    // this runs when the component unmounts so that we can cancel the debounce
    useEffect(() => {
      return () => {
        debouncedUpdateRedux.cancel();
      };
    }, [debouncedUpdateRedux]);

    useEffect(() => {
      if (!boxDetails.highlight) setHovering(false);
    }, [boxDetails.highlight]);

    useEffect(() => {
      if (tabTileProps.dashMode === 'Edit') {
        // SuppressING Redux hover dispatches if ANY tile is being interacted with
        if (isAnyTileInteracting) {
          return;
        }
        if (!hovering) {
          dispatch(clearHoveredTile());
        } else {
          dispatch(setHoveredTile(propKey));
        }
      }
    }, [hovering, propKey, isAnyTileInteracting]);

    // update local size when box details change
    useEffect(() => {
      setLocalSize({
        width: boxDetails.width * gridSize.x,
        height: boxDetails.height * gridSize.y,
      });
    }, [boxDetails.width, boxDetails.height, gridSize.x, gridSize.y]);

    const handleDragStart = (e: any, d: any) => {
      setIsDragging(true);
      setInteractingTile(propKey);
    };

    const handleDragStop = (e: any, d: any) => {
      setIsDragging(false);
      setInteractingTile(null);
      const newX = Math.round(d.x / gridSize.x);
      const newY = Math.round(d.y / gridSize.y);

      updateDashGraphPos(tabId, boxDetails.propKey, newX, newY);
      setStyle({ ...style, border: '1px solid gray' });
    };

    const handleResizeStart = (e: any, direction: any, ref: any) => {
      setIsResizing(true);
      setInteractingTile(propKey);
    };

    const handleResizeStop = (e: any, direction: any, ref: any, delta: any, position: any) => {
      debouncedUpdateRedux.cancel();
      setIsResizing(false);
      setInteractingTile(null);

      const widthInt = Number(ref.style.width.replace('px', ''));
      const heightInt = Number(ref.style.height.replace('px', ''));

      updateDashGraphSz(
        tabId,
        boxDetails.propKey,
        position.x / gridSize.x,
        position.y / gridSize.y,
        widthInt / gridSize.x,
        heightInt / gridSize.y,
      );
    };
    const handleResize = (e: any, direction: any, ref: any, delta: any, position: any) => {
      const widthPx = parseInt(ref.style.width, 10);
      const heightPx = parseInt(ref.style.height, 10);

      // Update local state immediately (fast)
      setLocalSize({ width: widthPx, height: heightPx });

      // Queue the Redux update (debounced)
      debouncedUpdateRedux(
        tabId,
        boxDetails.propKey,
        position.x / gridSize.x,
        position.y / gridSize.y,
        widthPx / gridSize.x,
        heightPx / gridSize.y,
      );
    };

    const handleTileClick = () => {
      setShowObjectsSidebar(true);
      setShowDashboardFilterEdit(false);
      setDashboardResizeColumn(false);
      setShowListofTileMenu(false);
      dispatch(setSelectedTile(propKey));

      // Also update tabTileProps.selectedTileId so ChartControlObjects shows the correct tile's controls
      const tileId = parseInt(propKey.split('.')[1], 10);
      const tileName = `Tile - ${tileId}`;
      dispatch(updateSelectedTile(tileName, tileId, tileId + 1));
    };

    return (
      // Drag and resize component
      <Rnd
        className={`${isHovered && tabTileProps.dashMode === 'Edit' ? 'dashboard-graph-hover' : ''} ${
          isSelected && tabTileProps.dashMode === 'Edit' ? 'dashboard-graph-selected' : ''
        } dashboard-graph `}
        disableDragging={
          tabTileProps.dashMode === 'Edit'
            ? chartControls.properties[boxDetails.propKey].cardControls.isDragging
              ? true
              : false
            : true
        }
        enableResizing={tabTileProps.dashMode === 'Edit' ? true : false}
        onMouseEnter={() => {
          if (tabTileProps.dashMode === 'Edit') {
            setHovering(true);
          }
        }}
        onMouseLeave={() => {
          if (!boxDetails.highlight) setHovering(false);
        }}
        bounds="parent"
        // units of pixels to move each time when this component is dragged or resized
        dragGrid={[dragGridX, dragGridY]}
        resizeGrid={[resizeGridX, resizeGridY]}
        minWidth={gridSize.x * 2}
        minHeight={gridSize.y * 2}
        // Compute the width * height based on number of background grids each component takes
        //style={boxDetails.highlight || hovering ? style2 : style}
        style={{
          ...(boxDetails.highlight || hovering ? extendedStyle2 : extendedStyle),
          zIndex: isDragging ? 1000 : 1,
        }}
        size={{
          width: boxDetails.width * gridSize.x,
          height: boxDetails.height * gridSize.y,
        }}
        position={{ x: boxDetails.x * gridSize.x, y: Math.max(0, boxDetails.y * gridSize.y) }}
        onDragStart={handleDragStart}
        onDragStop={handleDragStop} // Use the modified handler
        onResize={handleResize}
        onResizeStart={handleResizeStart}
        onResizeStop={handleResizeStop}
        dragHandleClassName={
          chartProp.properties[boxDetails.propKey].chartType === 'simplecard'
            ? 'simpleCardDragHandle'
            : 'dragHeader'
        }
      >
        {/* <div className="rndObject" propKey={boxDetails.propKey}> */}

        {chartProp.properties[boxDetails.propKey].chartType === 'simplecard' ? (
          <div
            className="rndObject simpleCardDragHandle"
            style={softUI ? skeuomorphicStyles : { borderRadius: borderRadius }}
            onMouseUp={handleTileClick}
          >
            <div
              style={{
                cursor: tabTileProps.dashMode === 'Present' ? 'default' : 'move',
                border: 'none',
                fontWeight: 'unset',
                color: 'unset',
                fontFamily: 'unset',
                height: '100%',
                width: '100%',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                backgroundColor: backgroundColor,
              }}
            >
              <div
                className="dashChart"
                id="dashChart"
                // propKey={boxDetails.propKey}
              >
                <div
                  style={{
                    height: '100%',
                    width: '100%',
                    pointerEvents: isAnyTileInteracting ? 'none' : 'auto',
                  }}
                >
                  {showWarning && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '5px',
                        right: '5px',
                      }}
                    >
                      <Tooltip
                        arrow
                        placement="top"
                        title={
                          'This chart cannot be rendered because one or more required fields are missing from the dataset. Please update the dataset to include the missing fields or edit the chart settings to use available fields.'
                        }
                        slotProps={{
                          tooltip: {
                            sx: {
                              fontSize: fontSize.medium,
                            },
                          },
                        }}
                      >
                        <Warning
                          style={{
                            color: '#850000ff',
                            flexShrink: 0,
                          }}
                          fontSize="small"
                        />
                      </Tooltip>
                    </div>
                  )}
                  <DashGraph
                    propKey={boxDetails.propKey}
                    tabId={tabId}
                    gridSize={gridSize}
                    colorScheme={schemeToUse}
                    softUI={softUI}
                    setMapJSON={setMapJSON}
                    setLoading={setLoading}
                    overrideDimensions={isResizing ? localSize : null}
                  />
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div
            className="rndObject"
            style={softUI ? skeuomorphicStyles : { ...(borderRadius ? { borderRadius } : {}) }}
            onClick={handleTileClick}
          >
            <div
              className={
                tabTileProps.dashMode === 'Present' ? 'dragHeaderForPresentMode' : 'dragHeader'
              }
              style={{
                cursor: tabTileProps.dashMode === 'Present' ? 'default' : 'move',
                fontSize:
                  dashboardWidth < 800
                    ? Math.min(14, chartProp.properties[boxDetails.propKey].titleOptions.fontSize)
                    : chartProp.properties[boxDetails.propKey].titleOptions.fontSize,
                backgroundColor: !backgroundColor ? 'white' : backgroundColor,
                minHeight: '18px',
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                width: '100%',
              }}
            >
              <span
                style={{
                  display: 'block',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '100%',
                }}
                title={chartProp.properties[boxDetails.propKey].titleOptions.chartTitle}
              >
                {chartProp.properties[boxDetails.propKey].titleOptions.chartTitle}
              </span>
              {showWarning && (
                <div
                  style={{
                    position: 'absolute',
                    right: '5px',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Tooltip
                    arrow
                    placement="top"
                    title={
                      'This chart cannot be rendered because one or more required fields are missing from the dataset. Please update the dataset to include the missing fields or edit the chart settings to use available fields.'
                    }
                    slotProps={{
                      tooltip: {
                        sx: {
                          fontSize: fontSize.medium,
                        },
                      },
                    }}
                  >
                    <Warning
                      style={{
                        color: '#850000ff',
                        flexShrink: 0,
                      }}
                      fontSize="small"
                    />
                  </Tooltip>
                </div>
              )}
              {(showRowSecurity || showColumnSecurity) && (
                <div
                  style={{
                    position: 'absolute',
                    right: '5px',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Tooltip
                    title={
                      showRowSecurity && showColumnSecurity
                        ? 'Dimension and Measure Level Control is applied'
                        : showRowSecurity
                          ? 'Dimension Level Control is applied'
                          : showColumnSecurity
                            ? 'Measure Level Control is applied'
                            : ''
                    }
                  >
                    <LockOutlinedIcon style={{ color: 'rgb(204 204 204)' }} />
                  </Tooltip>
                </div>
              )}
            </div>

            <div
              className="dashChart"
              style={{
                backgroundColor: backgroundColor,
                ...(softUI ? innerSkeuomorphicStyles : {}),
              }}
              id="dashChart"
              // propKey={boxDetails.propKey}
            >
              <div
                style={{
                  height: '100%',
                  width: '100%',
                  pointerEvents: isAnyTileInteracting ? 'none' : 'auto',
                }}
              >
                <DashGraph
                  propKey={boxDetails.propKey}
                  tabId={tabId}
                  gridSize={gridSize}
                  colorScheme={schemeToUse}
                  softUI={softUI}
                  setMapJSON={setMapJSON}
                  setLoading={setLoading}
                  fontFamily={fontFamily}
                  overrideDimensions={isResizing ? localSize : null}
                />
              </div>
            </div>
          </div>
        )}
      </Rnd>
    );
  },
);

const mapStateToProps = (state: any) => {
  return {
    tabTileProps: state.tabTileProps,
    chartProp: state.chartProperties,
    chartControls: state.chartControls,
    tabState: state.tabState,
  };
};

const mapDispatchToProps = (dispatch: Dispatch<any>) => {
  return {
    updateDashGraphPos: (tabId: number, propKey: string, x: any, y: any) =>
      dispatch(updateDashGraphPosition(tabId, propKey, x, y)),
    updateDashGraphSz: (tabId: number, propKey: string, x: any, y: any, width: any, height: any) =>
      dispatch(updateDashGraphSize(tabId, propKey, x, y, width, height)),
    // graphHighlight: (tabId: number, propKey: string) =>
    // 	dispatch(updateGraphHighlight(tabId, propKey)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(GraphRNDDash);
